import invariant from 'invariant';
import { useCallback, useEffect, useRef } from 'react';
import { styled } from 'styled-components';

import { useDebouncedFn } from '#/hooks/useDebouncedFn';
import { setGMapsError } from '#/zustand-stores/gMapsErrorStore';

// Google Maps map ID
// @see https://developers.google.com/maps/documentation/javascript/advanced-markers/start#create_a_map_id
const MAP_ID = '9464166ea36eadce';

const StyledMap = styled.div`
  height: 300px;
  margin-bottom: 1em;
  border-radius: 4px;
`;

type Props = {
  latitude: number;
  longitude: number;
  radius?: number | null;
  disabled?: boolean;
  qaId: string;
  updateAddress: (address: { latitude: number; longitude: number }) => void;
};

export function AddressMap({
  latitude,
  longitude,
  radius,
  disabled = false,
  qaId,
  updateAddress,
}: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | null>(null);
  const marker = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const circle = useRef<google.maps.Circle | null>(null);

  const createCircle = useCallback(
    (maps: typeof google.maps) => {
      invariant(marker.current, 'Marker is null');

      const position = marker.current.position as google.maps.LatLng;
      const mapCircle = new maps.Circle({
        map: map.current,
        radius,
        strokeColor: 'blue',
        strokeOpacity: 0.6,
        strokeWeight: 1,
        fillColor: 'blue',
        fillOpacity: 0.25,
        center: position,
      });

      if (map.current) {
        const circleBounds = mapCircle.getBounds();
        if (circleBounds !== null) {
          map.current.fitBounds(circleBounds);
        }
      }

      circle.current = mapCircle;
    },
    [radius],
  );

  const updateMap = useCallback(
    ({ showCircle }: { showCircle: boolean }) => {
      try {
        if (map.current) {
          map.current.setCenter({ lat: latitude, lng: longitude });
          if (showCircle && radius) {
            if (circle.current) {
              circle.current.setRadius(radius);
              circle.current.setCenter({ lat: latitude, lng: longitude });
              const circleBounds = circle.current.getBounds();
              if (circleBounds !== null) {
                map.current.fitBounds(circleBounds);
              }
            } else {
              createCircle(google.maps);
            }
          } else if (circle.current) {
            circle.current.setMap(null);
            circle.current = null;
          }
        }

        if (marker.current) {
          marker.current.position = { lat: latitude, lng: longitude };
        }
      } catch (error) {
        if (error instanceof Error) {
          setGMapsError(error.message);
        }
      }
    },
    [latitude, longitude, radius, createCircle],
  );

  const createMap = useCallback(async () => {
    try {
      const { Map } = (await google.maps.importLibrary(
        'maps',
      )) as google.maps.MapsLibrary;
      const { AdvancedMarkerElement } = (await google.maps.importLibrary(
        'marker',
      )) as google.maps.MarkerLibrary;
      const mapEl = mapRef.current;
      // If the user changes addresses before a map can render
      if (!mapEl) return;
      const position = { lat: latitude, lng: longitude };
      map.current = new Map(mapEl, {
        mapId: MAP_ID,
        center: position,
        zoom: 15,
        scrollwheel: false,
      });
      marker.current = new AdvancedMarkerElement({
        map: map.current,
        position,
        gmpDraggable: !disabled,
      });

      if (radius) {
        createCircle(google.maps);
      }

      invariant(marker.current, 'Marker is null');
      marker.current.addListener('dragend', () => {
        const markerPosition = marker.current
          ?.position as google.maps.LatLngLiteral;
        if (circle.current) {
          circle.current.setCenter(markerPosition);
        }
        updateAddress({
          latitude: markerPosition.lat,
          longitude: markerPosition.lng,
        });
      });
    } catch (error) {
      if (error instanceof Error) {
        setGMapsError(error.message);
      }
    }
  }, [latitude, longitude, radius, disabled, updateAddress, createCircle]);

  const debouncedUpdateMap = useDebouncedFn(updateMap, 200);

  const onResize = useCallback(() => {
    debouncedUpdateMap({
      showCircle: circle.current !== null,
    });
  }, [debouncedUpdateMap]);

  useEffect(() => {
    createMap();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [createMap, onResize]);

  return <StyledMap ref={mapRef} data-qa-id={qaId} />;
}
